import { Textbox } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FieldsEditor } from "@/components/planning/fields/FieldsEditor";
import { useScopeOptions } from "@/components/planning/fields/scopes";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import { EmptyState, RowSkeleton, Select } from "@/components/ui";
import { useCatalogState } from "@/data/CatalogProvider";

export const Route = createFileRoute("/_app/admin/fields")({
  validateSearch: (s: Record<string, unknown>) => ({ scope: typeof s.scope === "string" ? s.scope : undefined }),
  component: FieldsAdminPage,
});

/**
 * Administration: Fields and tags of every scope. The scope picker lists the instance, each space
 * and each product; Fields of a scope apply to every product below it.
 */
function FieldsAdminPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.fields_title() }]);
  const { catalog, isPending } = useCatalogState();
  const options = useScopeOptions(catalog);
  const { scope } = Route.useSearch();
  const navigate = useNavigate();
  const current = options.find((o) => o.id === scope) ?? options[0];

  return (
    <Page title={m.fields_title()} icon={Textbox} meta={m.fields_admin_meta()} width="full">
      {isPending && options.length === 0 ? (
        <RowSkeleton rows={4} />
      ) : !current ? (
        <EmptyState icon={Textbox} title={m.fields_no_products_title()} description={m.fields_no_products_body()} />
      ) : (
        <div className="flex flex-col gap-6">
          <Select
            label={m.fields_scope_label()}
            className="max-w-sm"
            items={options.map((o) => ({
              id: o.id,
              label:
                o.scope === "instance"
                  ? o.label
                  : o.scope === "space"
                    ? m.fields_scope_space_option({ name: o.label })
                    : m.fields_scope_product_option({ name: o.label }),
            }))}
            selectedKey={current.id}
            onSelectionChange={(k) => void navigate({ to: "/admin/fields", search: { scope: k }, replace: true })}
          />
          <FieldsEditor key={current.id} target={current} />
        </div>
      )}
    </Page>
  );
}
